import { motion } from 'framer-motion';
import { ChefHat, Heart, Award, Clock, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

// User-provided images
const userImages = {
  product1: 'https://customer-assets.emergentagent.com/job_madcakes/artifacts/ukilq0v9_WhatsApp%20Image%202026-02-06%20at%2018.51.06.jpeg',
  product2: 'https://customer-assets.emergentagent.com/job_madcakes/artifacts/yd9nyna5_WhatsApp%20Image%202026-02-06%20at%2018.50.23.jpeg',
  storefront: 'https://customer-assets.emergentagent.com/job_madcakes/artifacts/aer2b31i_WhatsApp%20Image%202026-02-06%20at%2018.50.01.jpeg',
  exterior: 'https://customer-assets.emergentagent.com/job_madcakes/artifacts/d1usdq8a_WhatsApp%20Image%202026-02-06%20at%2018.49.24.jpeg',
};

export default function AboutPage() {
  const values = [
    {
      icon: Heart,
      title: 'Made with Love',
      description: 'Every bake is crafted with the same passion that has defined our family for over 30 years.'
    },
    {
      icon: Award,
      title: 'Quality First',
      description: 'We use only the finest ingredients, sourced locally where possible, to ensure exceptional taste.'
    },
    {
      icon: Clock,
      title: 'Fresh Daily',
      description: 'Our bakes are made fresh to order, ensuring you receive the best quality every time.'
    },
    {
      icon: Users,
      title: 'Family Tradition',
      description: 'Three generations of bakers have perfected our recipes, passed down with pride.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="about-page">
      <SEO
        title="Our Story - 30+ Years of Artisan Baking in Dublin"
        description="Mad Brothers Bakery is a family-run artisan bakery in Dublin 7 (Ashtown) with over 30 years of tradition. Learn our story of handcrafted celebration cakes, communion cakes and Irish baking heritage."
        keywords="Mad Brothers Bakery story, family bakery Dublin, artisan bakery Ashtown, Dublin 7 bakery, Irish family bakery"
      />
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <img
          src={userImages.exterior}
          alt="Mad Brothers Bakery exterior"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#1a1a1a]/60" />
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <span className="font-handwritten text-2xl text-[#D4AF37]">Our Story</span>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold mt-2">
              About Mad Brothers
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={userImages.product1}
                      alt="Fresh baked goods"
                      className="w-full h-48 object-cover"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={userImages.storefront}
                      alt="Bakery interior"
                      className="w-full h-64 object-cover"
                    />
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={userImages.product2}
                      alt="Bakery display"
                      className="w-full h-64 object-cover"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden bg-[#8D6E63] p-6 flex flex-col items-center justify-center text-white">
                    <span className="font-['Playfair_Display'] text-4xl font-bold">30+</span>
                    <span className="text-sm mt-1">Years of Baking</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <span className="font-handwritten text-2xl text-[#8D6E63]">Our Heritage</span>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1a1a1a]">
                A Family Legacy of Excellence
              </h2>
              <p className="text-stone-600 leading-relaxed">
                The Mad Brothers story began over 30 years ago when our grandfather first 
                opened his small bakery. What started as a humble family venture has grown 
                into a tradition of excellence, passed down through three generations of 
                passionate bakers.
              </p>
              <p className="text-stone-600 leading-relaxed">
                In 2021, we brought our treasured family recipes to Dublin 7, establishing 
                Mad Brothers Bakery at 17 Nephin Road, Ashtown. Here, we continue our legacy 
                of creating exceptional baked goods, combining time-honored techniques with 
                a modern touch.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Every cake, every pastry, every loaf of bread is crafted with the same love 
                and dedication that our grandfather instilled in us. We believe that baking 
                is more than just a craft—it's an expression of love, meant to bring people 
                together and create lasting memories.
              </p>
              
              <div className="pt-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#1a1a1a] rounded-full flex items-center justify-center">
                    <ChefHat className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1a1a1a]">The Mad Brothers</p>
                    <p className="text-sm text-stone-500">Third Generation Bakers</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="font-handwritten text-2xl text-[#8D6E63]">What We Stand For</span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1a1a1a] mt-2">
              Our Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center p-6"
              >
                <div className="w-16 h-16 bg-[#8D6E63]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-[#8D6E63]" />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1a1a1a] mb-2">
                  {value.title}
                </h3>
                <p className="text-stone-500 text-sm">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1a1a1a] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl font-bold">
            Ready to Taste the Tradition?
          </h2>
          <p className="text-stone-300 mt-4">
            Order your first cake and experience 30 years of family baking excellence.
          </p>
          <Link to="/shop">
            <Button className="mt-8 bg-white text-[#1a1a1a] hover:bg-white/90 rounded-full px-10">
              Shop Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
